import {inject, Injectable, signal} from "@angular/core";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";

import {catchError, map, Observable, of, tap} from "rxjs";

import {DateTime} from "luxon";

import {environment} from "@environments/environment";
import {
    DaysData,
    DaysDataMonth,
    HistoryRecord,
    HistoryResponse,
    PvdTimeline,
    RequestConfig,
    RequestState,
    RequestStatus,
} from "@shared/models";
import {CacheId, MessageType} from "@shared/enums";
import {transformTimestampToLastUpdated} from "@shared/functions";
import {ErrorHandlingService} from "../util/error-handling.service";
import {ApiCacheService} from "../util/api-cache.service";
import {getContractualMonth} from "@shared/functions/date.functions";
import {GlobalService} from "../environment/global.service";

@Injectable({
  providedIn: "root",
})
export class HistoryApiService {
  apiCacheService = inject(ApiCacheService);
  http = inject(HttpClient);
  errorHandlingService = inject(ErrorHandlingService);
  globalService = inject(GlobalService);

  private readonly _baseUrl = environment.imaxApiUrl;

  private _historyRequestConfig: RequestConfig = {
    url: `${this._baseUrl}/GetHistory`,
    cacheId: CacheId.History,
    cacheValidMinutes: 15,
    forceRefresh: false,
    arrayKey: "history",
  };

  timestamp = signal<string>("");
  lastUpdatedText = signal<string>("");

  /**
   * Uses valid cached API history response or fetches a new one.
   * @param {boolean} force - Forces a new API call.
   * @returns {Observable<RequestState<HistoryResponse>>} Observable of {@link RequestState}<{@link HistoryResponse}>.
   */
  getHistory(force: boolean, refreshRecord?: number): Observable<RequestState<HistoryResponse>> {
    this._historyRequestConfig.forceRefresh = force;
    this._historyRequestConfig.url = `${this._baseUrl}/GetHistory${refreshRecord ? `?refresh=${refreshRecord}` : ""}`;
    return this.apiCacheService
      .request<HistoryResponse>(this._historyRequestConfig)
      .pipe(tap((response) => this.handleTimestamp(response)));
  }
  /**
   * Takes a {@link HistoryRecord} and sends it to the API to update the record.
   * If successful, the record is updated in the cached history response and returned
   * as a {@link RequestState}<{@link HistoryResponse}>
   * @param {HistoryRecord} record - The record to update.
   * @param {HistoryResponse} history - The cached history response.
   * @returns {Observable<RequestState<HistoryResponse>>} Observable of {@link RequestState}<{@link HistoryResponse}>.
   */
  updateHistoryRecord(record: HistoryRecord, history: HistoryResponse): Observable<RequestState<HistoryResponse>> {
    this.globalService.saving.set(true);
    const { createdDate, username, lastUpdated, ...recordToUpdate } = record;
    const historyCopy = { ...history };

    const handleResponse = (updatedRecord: HistoryRecord): RequestState<HistoryResponse> => {
      this.globalService.saving.set(false);
      if (Object.keys(updatedRecord).length === 0) {
        return new RequestState(RequestStatus.HasErrorWithCachedResponse, historyCopy);
      }
      const updatedHistory = { ...historyCopy };
      const index = updatedHistory.history.findIndex((r) => r.historyID === updatedRecord.historyID);
      updatedHistory.history[index] = updatedRecord;
      updatedHistory.timestamp = new Date().toISOString();
      localStorage.setItem(CacheId.History, JSON.stringify(updatedHistory));
      return new RequestState(RequestStatus.IsCompleteWithResponse, updatedHistory);
    };

    const handleError = (error: HttpErrorResponse): Observable<RequestState<HistoryResponse>> => {
      this.globalService.saving.set(false);
      if (error.status === 404) {
        this.errorHandlingService.sendMessage(MessageType.UpdateRecordNotFound);
        return of(new RequestState(RequestStatus.HasErrorWithCachedResponse, historyCopy));
      } else if (error.status === 500) {
        this.errorHandlingService.sendMessage(MessageType.ServerError);
        return of(new RequestState(RequestStatus.HasErrorWithCachedResponse, historyCopy));
      } else {
        this.errorHandlingService.sendMessage(MessageType.UnknownError);
        return of(new RequestState(RequestStatus.HasErrorWithCachedResponse, historyCopy));
      }
    };

    return this.http.post<HistoryRecord>(`${this._baseUrl}/UpdateHistory`, recordToUpdate).pipe(
      map((response) => handleResponse(response)),
      tap((response) => this.handleTimestamp(response)),
      catchError((error: HttpErrorResponse) => handleError(error))
    );
  }

  getDaysData(): Observable<DaysData> {
    return this.getHistory(false).pipe(
      map((response) => {
        if (response.isLoading || !response.response) {
          return {} as DaysData;
        } else {
          return this.parseDaysData(response.response);
        }
      })
    );
  }

  private parseDaysData(history: HistoryResponse): DaysData {
    const currentYear = new Date().getFullYear().toString();
    const currentMonth = Number(history.history[history.history.length - 1].effectivePeriod.toString().slice(5, 7));
    const baseYear = currentMonth <= 3 ? Number(currentYear) - 1 : Number(currentYear);
    const minEffectivePeriod = Number(`${baseYear}03`);
    const maxEffectivePeriod = Number(`${baseYear + 1}02`);
    const historyRecords = history.history;

    const parseValue = (key: keyof HistoryRecord): number =>
      historyRecords.reduce((acc, record) => {
        return record.effectivePeriod.toString().startsWith(currentYear) ? acc + (record[key] as number) : acc;
      }, 0);

    const parsePvds = (): number =>
      historyRecords.reduce((acc, record) => {
        return record.effectivePeriod >= minEffectivePeriod && record.effectivePeriod <= maxEffectivePeriod
          ? acc + (record.usedPVDs as number)
          : acc;
      }, 0);

    const parseMonths = (): DaysDataMonth[] => {
      const months: DaysDataMonth[] = [];
        console.log(historyRecords);
      const validHistoryRecords = historyRecords.filter((record) =>
        record.effectivePeriod.toString().startsWith(currentYear)
      );
      validHistoryRecords.forEach((record) => {
        months.push({
            month: getContractualMonth({month: Number(record.effectivePeriod.toString().slice(-2))}).monthShort,
          daysWorked: record.daysWorked,
          sick: record.usedSick,
          training: record.usedTraining,
        });
      });
      return months;
    };

    const parsePvdTimeline = (): PvdTimeline => {
      const pvdTimeline: PvdTimeline = {
        years: `${baseYear} - ${baseYear + 1}`,
        months: [],
      };
      historyRecords.forEach((record) => {
        if (record.effectivePeriod >= minEffectivePeriod && record.effectivePeriod <= maxEffectivePeriod) {
          const effectivePeriodMonth = Number(record.effectivePeriod.toString().slice(4, 6));
          const month = getContractualMonth({ month: effectivePeriodMonth }).monthShort;
          if (record.usedPVDs > 0) {
            pvdTimeline.months.push({
              month: month,
              pvds: record.usedPVDs > 0 ? record.usedPVDs.toString() : "-",
            });
          }
        }
      });
      return pvdTimeline;
    };

    return {
      timeline: currentYear,
      sick: parseValue("usedSick"),
      training: parseValue("usedTraining"),
      pvds: parsePvds(),
      pvdTimeline: parsePvdTimeline(),
      daysWorked: parseValue("daysWorked"),
      months: parseMonths(),
    };
  }

  private handleTimestamp(response: RequestState<HistoryResponse>): void {
    if (response.response) {
      const timestamp = DateTime.fromISO(response.response.timestamp);
      this.timestamp.set(response.response.timestamp);
      this.lastUpdatedText.set(transformTimestampToLastUpdated(timestamp));
    }
  }
}
